import { useCallback, useMemo } from 'react';
import { VODPurchase, VodTerm } from '../types/Asset';
import { useAuth } from '../providers/useAuth/AuthContext';
import { useVodPurchasesQuery } from './useVodPurchasesQuery';
import { isVodTermWatchable } from '../utils/fnEntitlement';
import { isFuture } from '../utils/fnDate';

export const HUUB_VOD_CATALOG_NAME = 'Huub';

const isPurchaseStillRelevant = (purchase: VODPurchase): boolean => {
    if (purchase?.valid_until) {
        return isFuture(purchase.valid_until * 1000);
    }

    if (purchase?.created_at && purchase?.vod_term?.rental_period) {
        return isFuture((purchase.created_at + purchase.vod_term.rental_period) * 1000);
    }

    return true;
};

type WatchableVodAsset = {
    id?: string;
    vod_terms?: VodTerm[];
    vod_session_until?: number;
};

export const useVodWatchable = (enabled = true) => {
    const { accountInfo } = useAuth();
    const { data: vodPurchases } = useVodPurchasesQuery(enabled);

    const entitledCatalogIds = useMemo(
        () => new Set((accountInfo?.vod_catalogs ?? []).map((catalog) => catalog.id)),
        [accountInfo?.vod_catalogs]
    );

    const entitledCatalogNames = useMemo(
        () => new Set((accountInfo?.vod_catalogs ?? []).map((catalog) => catalog.name)),
        [accountInfo?.vod_catalogs]
    );

    const hasVodCatalog = useCallback((catalogName: string): boolean => entitledCatalogNames.has(catalogName), [entitledCatalogNames]);

    const hasActivePurchase = useCallback(
        (assetId: string): boolean =>
            vodPurchases?.results?.some(
                (purchase: VODPurchase) => purchase.vod_asset?.id === assetId && isFuture(purchase.valid_until * 1000)
            ) ?? false,
        [vodPurchases]
    );

    const hasVodAssetPurchase = useCallback(
        (assetId?: string): boolean =>
            !!assetId &&
            (vodPurchases?.results?.some(
                (purchase: VODPurchase) => purchase.vod_asset?.id === assetId && isPurchaseStillRelevant(purchase)
            ) ??
                false),
        [vodPurchases]
    );

    const isVodAssetWatchable = useCallback(
        (assetId: string, vodTerms?: VodTerm[]): boolean =>
            vodTerms?.some((term) => isVodTermWatchable(term, assetId, entitledCatalogIds, hasActivePurchase)) ?? false,
        [entitledCatalogIds, hasActivePurchase]
    );

    const isVodEpisodeWatchable = useCallback(
        (episode?: WatchableVodAsset): boolean => {
            if (!episode?.id) {
                return false;
            }

            if (episode.vod_session_until > 0) {
                return true;
            }

            return episode.vod_terms?.length ? isVodAssetWatchable(episode.id, episode.vod_terms) : hasVodCatalog(HUUB_VOD_CATALOG_NAME);
        },
        [isVodAssetWatchable, hasVodCatalog]
    );

    return { isVodAssetWatchable, hasVodCatalog, isVodEpisodeWatchable, hasVodAssetPurchase };
};
